import { VehicleValuationStatus, VehicleValuationType } from "@/api-schema-enums";
import { openInNewTab } from "@/common/helpers/open-in-new-tab";
import { cn, formatNameForDisplay } from "@/common/helpers/utils";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyValue } from "@/components/ui/empty-value";
import { UserItem, UserItemVariant } from "@/components/user-item/user-item";
import { VehicleValuationDateIndicator } from "@/components/vehicle-valuation/valuation-date-indicator";
import { VehicleValuationMileageDisplay } from "@/components/vehicle-valuation/vehicle-valuation-mileage-display";
import { VehicleValuationProviderBadge } from "@/components/vehicle-valuation/vehicle-valuation-provider-badge";
import { VehicleValuationReimportDisplay } from "@/components/vehicle-valuation/vehicle-valuation-reimport-display";
import { VehicleValuationSnapshotAction } from "@/components/vehicle-valuation/vehicle-valuation-snapshot-action";
import { VehicleValuationStatusBadge } from "@/components/vehicle-valuation/vehicle-valuation-status-badge";
import { getImpliedConfirmedNetValue } from "@/modules/fab/vehicle-valuation/_helpers/get-implied-confirmed-net-value";
import {
  getValuationNumberDifference,
  roundVehicleValuationPriceForDisplay,
} from "@/modules/fab/vehicle-valuation/_helpers/vehicle-valuation-derived-values";
import {
  getVehicleAccidentStateLabel,
  getVehicleBooleanLabel,
  getVehicleValuationRimTypeLabel,
  getVehicleValuationTypeLabel,
  shouldShowVehicleAccidentDetails,
} from "@/modules/fab/vehicle-valuation/_helpers/vehicle-valuation-metadata";
import { DetailInfoRow } from "@/views/vehicle-valuation-detail/_components/detail-info-row";
import { ValuationComparisonRow } from "@/views/vehicle-valuation-detail/_components/valuation-comparison-row";
import { AlertTriangle, DownloadIcon, EyeIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import {
  formatIntegerText,
  type VehicleValuation,
  type VehicleValuationHistoryEntry,
} from "../_helpers/vehicle-valuation-history.helpers";

type AutomaticCellRendererOptions = NonNullable<Parameters<typeof automaticCellRenderer>[1]>;

function renderVehicleValuationUser(
  user: VehicleValuation["requester"] | VehicleValuation["confirmedBy"] | string | null | undefined,
): ReactNode {
  if (!user) {
    return undefined;
  }

  if (typeof user === "string") {
    return formatNameForDisplay(user) || user;
  }

  return <UserItem user={user} variant={UserItemVariant.Compact} />;
}

function renderDifferenceValue(value?: number | null, options?: AutomaticCellRendererOptions) {
  if (value === null || value === undefined) {
    return <EmptyValue />;
  }

  return (
    <span
      className={cn(
        "text-sm font-medium",
        value > 0 ? "text-success" : undefined,
        value < 0 ? "text-error" : undefined,
        value === 0 ? "text-muted-foreground" : undefined,
      )}
    >
      {value > 0 ? "+" : ""}
      {automaticCellRenderer(value, options)}
    </span>
  );
}

export function renderVehicleValuationMileageValue(valuation?: VehicleValuation | null) {
  return (
    <VehicleValuationMileageDisplay
      mileage={valuation?.mileageUsed}
      projectedMileage={valuation?.projectedMileage}
      targetDate={valuation?.valuationDateUsed}
    />
  );
}

function DifferenceMetricCard({
  label,
  value,
  options,
}: {
  label: string;
  value?: number | null;
  options?: AutomaticCellRendererOptions;
}) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-muted-foreground text-xs font-medium">{label}</div>
      <div className="mt-1">{renderDifferenceValue(value, options)}</div>
    </div>
  );
}

export function VehicleValuationHistoryCard({
  entry,
  canManageVehicleValuation,
}: {
  entry: VehicleValuationHistoryEntry;
  canManageVehicleValuation: boolean;
}) {
  const { t } = useTranslation(["common", "masterdata", "fab"]);
  const { valuation, previous, mismatch } = entry;
  const mismatchTooltip = mismatch
    ? t("fab:VehicleValuationHistoryMismatchItem", {
        currentMileage: formatIntegerText(mismatch.current.mileageUsed),
        currentOwners: formatIntegerText(mismatch.current.numberOfHoldersUsed),
        referenceMileage: formatIntegerText(mismatch.reference.mileageUsed),
        referenceOwners: formatIntegerText(mismatch.reference.numberOfHoldersUsed),
      })
    : undefined;
  const shouldShowAccidentDetails = shouldShowVehicleAccidentDetails(valuation.accidentState);
  const isConfirmedValuation = valuation.status === VehicleValuationStatus.Confirmed;
  const shouldShowLeaseBuyoutAmount =
    isConfirmedValuation && valuation.valuationType === VehicleValuationType.LeasingBuyout;
  const shouldShowVehicleValuationType = isConfirmedValuation;
  const shouldShowConfirmedValuationAmounts = isConfirmedValuation && !shouldShowLeaseBuyoutAmount;

  const impliedConfirmedTradeNet = getImpliedConfirmedNetValue({
    originalNet: valuation.tradeNet,
    originalGross: valuation.tradeGross,
    confirmedGross: valuation.confirmedTradeGross,
  });

  const impliedConfirmedSalesNet = getImpliedConfirmedNetValue({
    originalNet: valuation.salesNet,
    originalGross: valuation.salesGross,
    confirmedGross: valuation.confirmedSalesGross,
  });

  const impliedConfirmedMarginNet = getImpliedConfirmedNetValue({
    originalNet: valuation.marginNet,
    originalGross: valuation.marginGross,
    confirmedGross: valuation.confirmedMarginGross,
  });
  const roundedConfirmedTradeGross = roundVehicleValuationPriceForDisplay(valuation.confirmedTradeGross);
  const roundedConfirmedSalesGross = roundVehicleValuationPriceForDisplay(valuation.confirmedSalesGross);
  const roundedConfirmedMarginGross = roundVehicleValuationPriceForDisplay(valuation.confirmedMarginGross);
  const roundedImpliedConfirmedTradeNet = roundVehicleValuationPriceForDisplay(impliedConfirmedTradeNet);
  const roundedImpliedConfirmedSalesNet = roundVehicleValuationPriceForDisplay(impliedConfirmedSalesNet);
  const roundedImpliedConfirmedMarginNet = roundVehicleValuationPriceForDisplay(impliedConfirmedMarginNet);

  const tradeGrossDifference = getValuationNumberDifference(valuation.tradeGross, previous?.tradeGross);
  const salesGrossDifference = getValuationNumberDifference(valuation.salesGross, previous?.salesGross);
  const marginGrossDifference = getValuationNumberDifference(valuation.marginGross, previous?.marginGross);
  const marginPercentageDifference = getValuationNumberDifference(
    valuation.marginPercentage,
    previous?.marginPercentage,
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <VehicleValuationStatusBadge status={valuation.status} />
              <VehicleValuationProviderBadge provider={valuation.provider} />
              {mismatch ? (
                <Badge
                  variant={BadgeVariant.Outlined}
                  colorVariant={BadgeColorVariant.Warning}
                  tooltip={mismatchTooltip}
                >
                  {t("fab:NumberOfHoldersMismatchBadge")}
                </Badge>
              ) : null}
            </div>

            <div>
              <CardTitle className="flex flex-wrap items-center gap-2">
                <span>{t("masterdata:Mileage")}:</span>
                {renderVehicleValuationMileageValue(valuation)}
              </CardTitle>
              <CardDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>{automaticCellRenderer(valuation.createdAt, { dateFormat: "datetime" })}</span>
                <span aria-hidden="true">•</span>
                <span className="inline-flex items-center gap-1">
                  <span>
                    {t("fab:VehicleValuationTargetDate")}: {automaticCellRenderer(valuation.valuationDateUsed)}
                  </span>
                  <VehicleValuationDateIndicator
                    value={valuation.valuationDateUsed}
                    referenceDate={valuation.createdAt}
                    reserveSpace
                  />
                </span>
              </CardDescription>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              asChild
              size={ButtonSize.Sm}
              variant={ButtonVariant.Outlined}
              colorVariant={ButtonColorVariant.Secondary}
            >
              <Link to={`~/fab/list/${valuation.uuid}`}>
                <EyeIcon className="h-4 w-4" />
                {t("fab:ViewVehicleValuation")}
              </Link>
            </Button>

            {valuation.reportUrl ? (
              <Button
                size={ButtonSize.Sm}
                type="button"
                variant={ButtonVariant.Outlined}
                colorVariant={ButtonColorVariant.Secondary}
                onClick={() => openInNewTab(valuation.reportUrl)}
              >
                <DownloadIcon className="h-4 w-4" />
                {t("fab:OpenValuationReport")}
              </Button>
            ) : null}

            <VehicleValuationSnapshotAction valuationUuid={valuation.uuid} size={ButtonSize.Sm} />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{t("fab:VehicleValuationInputs")}</h3>

            <div className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <DetailInfoRow
                  label={t("common:Status")}
                  value={<VehicleValuationStatusBadge status={valuation.status} />}
                />
                <DetailInfoRow
                  label={t("masterdata:Provider")}
                  value={<VehicleValuationProviderBadge provider={valuation.provider} />}
                />
                <DetailInfoRow
                  label={t("masterdata:Requester")}
                  value={renderVehicleValuationUser(valuation.requester)}
                />
                <DetailInfoRow label={t("fab:VIN")} value={valuation.vinUsed ?? <EmptyValue />} mono />
                <DetailInfoRow
                  label={t("common:UpdatedAt")}
                  value={automaticCellRenderer(valuation.updatedAt, { dateFormat: "datetime" })}
                />
              </div>

              <div className="grid gap-4 border-t pt-4 sm:grid-cols-2">
                <DetailInfoRow
                  label={t("common:RegistrationDate")}
                  value={automaticCellRenderer(valuation.registrationDateUsed)}
                />
                <DetailInfoRow
                  label={t("fab:VehicleValuationTargetDate")}
                  value={
                    <div className="flex items-center gap-2">
                      <span>{automaticCellRenderer(valuation.valuationDateUsed)}</span>
                      <VehicleValuationDateIndicator
                        value={valuation.valuationDateUsed}
                        referenceDate={valuation.createdAt}
                        reserveSpace
                      />
                    </div>
                  }
                />
                <DetailInfoRow label={t("masterdata:Mileage")} value={renderVehicleValuationMileageValue(valuation)} />
                <DetailInfoRow
                  label={t("masterdata:NumberOfHolders")}
                  value={automaticCellRenderer(valuation.numberOfHoldersUsed)}
                />
                <DetailInfoRow label={t("fab:RimType")} value={getVehicleValuationRimTypeLabel(valuation.rimType, t)} />
                <DetailInfoRow
                  label={t("masterdata:VatDeductible")}
                  value={getVehicleBooleanLabel(valuation.vatDeductible, t)}
                />
                <DetailInfoRow
                  label={t("masterdata:Reimport")}
                  value={<VehicleValuationReimportDisplay reimport={valuation.reimport} />}
                />
              </div>

              <div className="grid gap-4 border-t pt-4 sm:grid-cols-2">
                <DetailInfoRow
                  label={t("masterdata:AccidentState")}
                  value={getVehicleAccidentStateLabel(valuation.accidentState, t)}
                />
                {shouldShowAccidentDetails ? (
                  <DetailInfoRow
                    label={t("fab:AccidentValue")}
                    value={automaticCellRenderer(valuation.accidentValue, {
                      numberFormat: "currency",
                      numberDecimals: 2,
                    })}
                  />
                ) : null}
                {shouldShowAccidentDetails ? (
                  <DetailInfoRow
                    label={t("fab:AccidentDescription")}
                    fullWidth
                    value={
                      valuation.accidentDescription ? (
                        <div className="wrap-break-word whitespace-pre-wrap">{valuation.accidentDescription}</div>
                      ) : undefined
                    }
                  />
                ) : null}
              </div>

              <div className="grid gap-4 border-t pt-4 sm:grid-cols-2">
                <DetailInfoRow
                  label={t("fab:EarlyLeaseTermination")}
                  value={getVehicleBooleanLabel(valuation.earlyLeaseTermination, t)}
                />
                <DetailInfoRow
                  label={t("fab:EarlyLeaseTerminationAmount")}
                  value={automaticCellRenderer(valuation.earlyLeaseTerminationAmount, {
                    numberFormat: "currency",
                    numberDecimals: 2,
                  })}
                />
              </div>

              <div className="grid gap-4 border-t pt-4 sm:grid-cols-2">
                <DetailInfoRow
                  label={t("fab:VehicleValuationCreatorNote")}
                  fullWidth
                  value={
                    valuation.requesterNote ? (
                      <div className="wrap-break-word whitespace-pre-wrap">{valuation.requesterNote}</div>
                    ) : undefined
                  }
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{t("fab:VehicleValuationResult")}</h3>

            <div className="space-y-4">
              {canManageVehicleValuation ? (
                <div className="space-y-2">
                  <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                    {t("masterdata:SalesGroup")}
                  </div>
                  <div className="space-y-2 border-l-2 pl-3">
                    <ValuationComparisonRow
                      label={t("masterdata:Gross")}
                      originalValue={automaticCellRenderer(valuation.salesGross, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      confirmedValue={automaticCellRenderer(roundedConfirmedSalesGross, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      originalNumber={valuation.salesGross}
                      confirmedNumber={shouldShowConfirmedValuationAmounts ? roundedConfirmedSalesGross : undefined}
                    />
                    <ValuationComparisonRow
                      label={t("masterdata:Net")}
                      originalValue={automaticCellRenderer(valuation.salesNet, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      confirmedValue={automaticCellRenderer(roundedImpliedConfirmedSalesNet, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      originalNumber={valuation.salesNet}
                      confirmedNumber={
                        shouldShowConfirmedValuationAmounts ? roundedImpliedConfirmedSalesNet : undefined
                      }
                    />
                  </div>
                </div>
              ) : null}

              {canManageVehicleValuation ? (
                <div className="space-y-2">
                  <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                    {t("masterdata:MarginGroup")}
                  </div>
                  <div className="space-y-2 border-l-2 pl-3">
                    <ValuationComparisonRow
                      label={t("masterdata:Percentage")}
                      originalValue={automaticCellRenderer(valuation.marginPercentage, {
                        numberFormat: "percentage",
                        numberDecimals: 2,
                      })}
                      confirmedValue={automaticCellRenderer(valuation.confirmedMarginPercentage, {
                        numberFormat: "percentage",
                        numberDecimals: 2,
                      })}
                      originalNumber={valuation.marginPercentage}
                      confirmedNumber={
                        shouldShowConfirmedValuationAmounts ? valuation.confirmedMarginPercentage : undefined
                      }
                    />
                    <ValuationComparisonRow
                      label={t("masterdata:Gross")}
                      originalValue={automaticCellRenderer(valuation.marginGross, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      confirmedValue={automaticCellRenderer(roundedConfirmedMarginGross, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      originalNumber={valuation.marginGross}
                      confirmedNumber={shouldShowConfirmedValuationAmounts ? roundedConfirmedMarginGross : undefined}
                    />
                    <ValuationComparisonRow
                      label={t("masterdata:Net")}
                      originalValue={automaticCellRenderer(valuation.marginNet, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      confirmedValue={automaticCellRenderer(roundedImpliedConfirmedMarginNet, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      originalNumber={valuation.marginNet}
                      confirmedNumber={
                        shouldShowConfirmedValuationAmounts ? roundedImpliedConfirmedMarginNet : undefined
                      }
                    />
                  </div>
                </div>
              ) : null}

              {canManageVehicleValuation ? (
                <div className="space-y-2">
                  <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                    {t("masterdata:Other")}
                  </div>
                  <div className="space-y-2 border-l-2 pl-3">
                    <ValuationComparisonRow
                      label={t("masterdata:Warranty")}
                      originalValue={automaticCellRenderer(valuation.warranty, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      confirmedValue={automaticCellRenderer(valuation.confirmedWarranty, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      originalNumber={valuation.warranty}
                      confirmedNumber={shouldShowConfirmedValuationAmounts ? valuation.confirmedWarranty : undefined}
                    />
                    <ValuationComparisonRow
                      label={t("masterdata:VehiclePreparationCosts")}
                      originalValue={automaticCellRenderer(valuation.vehiclePreparationCosts, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      confirmedValue={automaticCellRenderer(valuation.confirmedVehiclePreparationCosts, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      originalNumber={valuation.vehiclePreparationCosts}
                      confirmedNumber={
                        shouldShowConfirmedValuationAmounts ? valuation.confirmedVehiclePreparationCosts : undefined
                      }
                    />
                  </div>
                </div>
              ) : null}

              {canManageVehicleValuation ? (
                <div className="space-y-2">
                  <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                    {t("masterdata:TradeGroup")}
                  </div>
                  <div className="space-y-2 border-l-2 pl-3">
                    <ValuationComparisonRow
                      label={t("masterdata:Gross")}
                      originalValue={automaticCellRenderer(valuation.tradeGross, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      confirmedValue={automaticCellRenderer(roundedConfirmedTradeGross, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      originalNumber={valuation.tradeGross}
                      confirmedNumber={shouldShowConfirmedValuationAmounts ? roundedConfirmedTradeGross : undefined}
                    />
                    <ValuationComparisonRow
                      label={t("masterdata:Net")}
                      originalValue={automaticCellRenderer(valuation.tradeNet, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      confirmedValue={automaticCellRenderer(roundedImpliedConfirmedTradeNet, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      originalNumber={valuation.tradeNet}
                      confirmedNumber={
                        shouldShowConfirmedValuationAmounts ? roundedImpliedConfirmedTradeNet : undefined
                      }
                    />
                  </div>
                </div>
              ) : isConfirmedValuation ? (
                <DetailInfoRow
                  label={shouldShowLeaseBuyoutAmount ? t("fab:LeaseBuyoutAmount") : t("masterdata:ConfirmedTradeGross")}
                  value={automaticCellRenderer(
                    shouldShowLeaseBuyoutAmount ? valuation.leaseBuyoutAmount : roundedConfirmedTradeGross,
                    {
                      numberFormat: "currency",
                      numberDecimals: 2,
                    },
                  )}
                  valueClassName={
                    shouldShowLeaseBuyoutAmount && valuation.leaseBuyoutAmount != null
                      ? "text-primary font-semibold"
                      : undefined
                  }
                />
              ) : null}

              {canManageVehicleValuation && shouldShowLeaseBuyoutAmount ? (
                <div className="space-y-2">
                  <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                    {t("fab:VehicleValuationTypeLeasingBuyout")}
                  </div>
                  <div className="space-y-2 border-l-2 pl-3">
                    <DetailInfoRow
                      label={t("fab:LeaseBuyoutAmount")}
                      value={automaticCellRenderer(valuation.leaseBuyoutAmount, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                      valueClassName={valuation.leaseBuyoutAmount != null ? "text-primary font-semibold" : undefined}
                    />
                  </div>
                </div>
              ) : null}

              {shouldShowVehicleValuationType ? (
                <DetailInfoRow
                  label={t("fab:VehicleValuationType")}
                  value={getVehicleValuationTypeLabel(valuation.valuationType, t)}
                />
              ) : null}

              {isConfirmedValuation ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <DetailInfoRow
                    label={t("masterdata:ConfirmedAt")}
                    value={automaticCellRenderer(valuation.confirmedAt, { dateFormat: "datetime" })}
                  />
                  <DetailInfoRow
                    label={t("masterdata:ConfirmedBy")}
                    value={renderVehicleValuationUser(valuation.confirmedBy)}
                  />
                </div>
              ) : null}

              {valuation.note ? (
                <DetailInfoRow
                  label={t("masterdata:VehicleValuationNote")}
                  value={<div className="wrap-break-word whitespace-pre-wrap">{valuation.note}</div>}
                />
              ) : null}
            </div>
          </div>
        </div>

        {canManageVehicleValuation ? (
          <div className="space-y-3 border-t pt-4">
            <h3 className="text-sm font-semibold">{t("fab:DifferenceToPreviousCalculation")}</h3>

            {previous ? (
              <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-4">
                <DifferenceMetricCard
                  label={t("fab:TradeGross")}
                  value={tradeGrossDifference}
                  options={{ numberFormat: "currency", numberDecimals: 2 }}
                />
                <DifferenceMetricCard
                  label={t("fab:SalesGross")}
                  value={salesGrossDifference}
                  options={{ numberFormat: "currency", numberDecimals: 2 }}
                />
                <DifferenceMetricCard
                  label={t("fab:MarginGross")}
                  value={marginGrossDifference}
                  options={{ numberFormat: "currency", numberDecimals: 2 }}
                />
                <DifferenceMetricCard
                  label={t("fab:MarginPercentage")}
                  value={marginPercentageDifference}
                  options={{ numberFormat: "percentage", numberDecimals: 2 }}
                />
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">{t("fab:NoPreviousVehicleValuationForComparison")}</p>
            )}
          </div>
        ) : null}

        {valuation.errorMessage ? (
          <Alert variant="error">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t("fab:ErrorMessage")}</AlertTitle>
            <AlertDescription>{valuation.errorMessage}</AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
